import { useApiMutation } from "@gladis/api-client";
import { Button, Dialog, Label, SelectField, TextField } from "@gladis/ui";
import { errorSentence, useT } from "@gladis/ui/i18n";
import { useState, type ReactNode } from "react";

import { SplitEditor, splitsAreSubmittable, type EditableSplit } from "./SplitEditor.js";
import { EXPENSES_KEY, type CardSummaryDto, type ExpenseRowDto } from "./ExpenseList.js";

/**
 * Who pays a mortgage row: which card it leaves from, and whose cost it is.
 *
 * ==========================================================================================
 * A DIALOG, OPENED FROM THE ROW
 * ==========================================================================================
 *
 * This lived inline in a read-only "Mortgage" section below the expenses table. The mortgage parts are rows
 * of that table now — one table reads better than a table plus a section — and a row has no space for a card
 * picker and a split editor, so the controls moved into a dialog, exactly as the expense form did.
 *
 * WHAT IT CANNOT CHANGE is every figure: the payment and the interest are computed from the mortgage part's
 * balance, rate and schedule on every read, and there is no input for either here. That is what "read-only"
 * meant and still means; what was never read-only, and was merely missing, is who pays.
 *
 * THE SPLIT EDITOR IS THE EXPENSE FORM'S, unchanged. The rules are the same rules — total exactly 100%,
 * frozen shares read-only and still counting — so sharing the control means the two cannot diverge in what
 * they accept, and a member who has entered one split already knows this one.
 */
export function AttributionDialog({
  open,
  onClose,
  line,
  members,
  cards,
  currentAnchor = null,
}: {
  open: boolean;
  onClose: () => void;
  /** The mortgage row being attributed, or null when the dialog is closed. */
  line: ExpenseRowDto | null;
  members: readonly { userId: string; displayName: string }[];
  cards: readonly CardSummaryDto[];
  /** The mortgage's first payment date in force today (feature 024), from the schedule read. */
  currentAnchor?: string | null;
}): ReactNode {
  const t = useT();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("exp.attribution.heading")}
      description={t("exp.attribution.hint")}
    >
      {/*
        Mounted only with a line to attribute. The dialog itself is always mounted — a native `<dialog>`
        cannot be conditionally rendered, per `Dialog`'s own note — but its FIELDS key on the row, so opening
        it on a second mortgage part does not show the first one's card.
      */}
      {line === null ? null : (
        <AttributionFields
          key={line.sourceId ?? line.id}
          line={line}
          members={members}
          cards={cards}
          currentAnchor={currentAnchor}
          onDone={onClose}
        />
      )}
    </Dialog>
  );
}

/**
 * The card picker and the split editor, for one line.
 *
 * THE SPLIT EDITOR IS THE EXPENSE FORM'S, unchanged. The rules are the same rules — total exactly 100%,
 * frozen shares read-only and still counting — so reusing it means the two cannot diverge in what they
 * accept, and a member who has entered a split once already knows this control.
 *
 * THE PICKER OFFERS ACTIVE CARDS ONLY, and keeps an archived one that is already set. Identical to the
 * expense form, for the reason FR-044 gives: an archived card keeps paying what it already pays, and the
 * only thing archiving takes away is being chosen anew.
 */
function AttributionFields({
  line,
  members,
  cards,
  currentAnchor,
  onDone,
}: {
  line: ExpenseRowDto;
  members: readonly { userId: string; displayName: string }[];
  cards: readonly CardSummaryDto[];
  currentAnchor: string | null;
  onDone: () => void;
}): ReactNode {
  const t = useT();
  const [cardId, setCardId] = useState(line.card?.id ?? "");
  const [splits, setSplits] = useState<EditableSplit[]>(
    line.splits.map((split) => ({
      memberId: split.memberId ?? "",
      memberDisplayName: split.memberDisplayName,
      percentage: split.percentage,
      isFrozen: split.isFrozen,
    })),
  );

  const save = useApiMutation<void, { cardId: string | null; splits: unknown[] }>(
    (client, input) =>
      client.put<void>(`/expenses/derived/${line.sourceId ?? ""}/attribution`, input),
    { invalidates: [EXPENSES_KEY] },
  );

  /*
   * THE DUE DATE IS ITS OWN REQUEST (feature 024, FR-008). A date is not "who pays": sending it through the
   * attribution PUT would create an attribution row and log "attributed" for a change that named nobody. So
   * Save sends the date only if the date changed, and the attribution only if the card or a share did.
   */
  // Measured against the date as the dialog opened: a schedule that loads afterwards must not read as a clear.
  const [openedWith] = useState(currentAnchor ?? "");
  const [paymentDate, setPaymentDate] = useState(openedWith);
  const saveDate = useApiMutation<void, { firstPaymentDate: string | null }>(
    (client, input) =>
      client.put<void>(`/expenses/derived/${line.sourceId ?? ""}/payment-date`, input),
    { invalidates: [EXPENSES_KEY] },
  );
  const initialSplits = JSON.stringify(
    line.splits.map((split) => [split.memberId, split.percentage]),
  );
  const attributionChanged =
    cardId !== (line.card?.id ?? "") ||
    JSON.stringify(splits.map((split) => [split.memberId, split.percentage])) !== initialSplits;
  const dateChanged = paymentDate !== openedWith;

  const clear = useApiMutation<void, void>(
    (client) => client.delete<void>(`/expenses/derived/${line.sourceId ?? ""}/attribution`),
    { invalidates: [EXPENSES_KEY] },
  );

  // Empty is submittable — it means the household's, which the settle-up reads as an equal share.
  const canSubmit = splits.length === 0 || splitsAreSubmittable(splits);

  const offered = cards.filter((card) => card.archivedAt === null || card.id === line.card?.id);

  return (
    <div>
      <div className="flex flex-col gap-2">
        <label htmlFor={`attribution-card-${line.sourceId}`} className="text-sm text-muted">
          {t("exp.list.colPaidFrom")}
        </label>
        <SelectField
          id={`attribution-card-${line.sourceId}`}
          value={cardId}
          onChange={(event) => setCardId(event.target.value)}
        >
          {/* Null is a real choice: whose cost it is, decided; which account, not. */}
          <option value="">{t("exp.form.notAttributed")}</option>
          {offered.map((card) => (
            <option key={card.id} value={card.id}>
              {card.identifier === null ? card.name : `${card.name} — ${card.identifier}`}
            </option>
          ))}
        </SelectField>
      </div>

      <div className="mt-4">
        <SplitEditor value={splits} members={members} onChange={setSplits} />
      </div>

      <div className="mt-4 flex flex-col gap-1">
        <Label htmlFor={`attribution-payment-${line.sourceId}`}>{t("exp.form.paymentDate")}</Label>
        <TextField
          id={`attribution-payment-${line.sourceId}`}
          type="date"
          value={paymentDate}
          onChange={(event) => {
            setPaymentDate(event.target.value);
          }}
        />
        <p className="text-xs text-muted">{t("exp.form.paymentDateHint")}</p>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          variant="primary"
          disabled={!canSubmit || save.isPending || saveDate.isPending}
          onClick={() => {
            void (async () => {
              try {
                if (dateChanged) {
                  await saveDate.mutateAsync({
                    firstPaymentDate: paymentDate === "" ? null : paymentDate,
                  });
                }
                if (attributionChanged) {
                  await save.mutateAsync({
                    cardId: cardId === "" ? null : cardId,
                    // Frozen shares are SUBMITTED, not dropped: the server permits a share for a member who
                    // has left only when it is already stored, and omitting it would fail the 100% check.
                    splits: splits.map((split) => ({
                      memberId: split.memberId,
                      percentage: split.percentage,
                    })),
                  });
                }
                onDone();
              } catch {
                // The failed mutation's own `isError` renders the sentence below; the dialog stays open.
              }
            })();
          }}
        >
          {save.isPending || saveDate.isPending ? t("common.saving") : t("common.save")}
        </Button>

        {line.card === null && line.splits.length === 0 ? null : (
          <button
            type="button"
            disabled={clear.isPending}
            onClick={() => {
              clear.mutate(undefined, { onSuccess: onDone });
            }}
            className="rounded-control px-1 text-sm text-muted underline decoration-dotted hover:text-ink focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:opacity-50"
          >
            {t("exp.attribution.clear")}
          </button>
        )}

        <button
          type="button"
          onClick={onDone}
          className="rounded-control px-1 text-sm text-muted underline decoration-dotted hover:text-ink focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
        >
          {t("common.cancel")}
        </button>

        {save.isError ? (
          <span className="text-sm text-danger">{errorSentence(save.error)}</span>
        ) : null}
        {saveDate.isError ? (
          <span className="text-sm text-danger">{errorSentence(saveDate.error)}</span>
        ) : null}
      </div>
    </div>
  );
}
